import { GovAddressBox, ISelectedAddressRecord } from "@publicMirage/components/govReact/GovAddressBox";
import { GovField } from "@publicMirage/components/govReact/GovField";
import { useForm } from "@prePublicPortalWestLindsay/pages/form/hooks/formContext";
import { Button, ErrorText, Fieldset, FormGroup, LabelText } from "govuk-react";
import React, { memo, useCallback, useEffect } from "react";
import { getValuesWithNewLines } from "@shared/mirage/utils/getValuesWithNewLines";
import { useBoolean } from "@mirage/hooks/useBoolean";
import { PrePpWlFormComponentStepProps } from "../PrePublicPortalWlForm";
import Box from "@mui/material/Box";

export const PrePpWlApplicantDetailsFormStep = memo((props: Omit<PrePpWlFormComponentStepProps, "previous">) => {
    const { next } = props;
    const { toFieldProps, setFieldValue, formState } = useForm();
    const { applicantAddressLine1, applicantAddressLine2, applicantAddressLine3, applicantPostTown, applicantPostcode, applicantAddress } = formState;
    const { value: addressEditable, toggle: toggleAddressEditable } = useBoolean(false);
    const updateGazetteerExtraFields = useCallback(
        (easting: string | number, northing: string | number, uprn: string) => {
            setFieldValue("applicantEasting", easting);
            setFieldValue("applicantNorthing", northing);
            setFieldValue("applicantUprn", uprn);
        },
        [setFieldValue]
    );
    const errorMsg = applicantAddress.errorMsg;
    useEffect(() => {
        if (addressEditable) {
            updateGazetteerExtraFields("", "", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantAddressLine1, applicantAddressLine2, applicantAddressLine3, applicantPostTown, applicantPostcode]);
    const gazeteerOnSelect = useCallback(
        ({ addressDetails, addressLines, point }: ISelectedAddressRecord) => {
            const { easting, northing } = point;
            const { UPRN, TOWN_NAME, POSTCODE } = addressDetails;
            const { line1, line2, line3 } = addressLines;
            const location = getValuesWithNewLines([line1, line2, line3, TOWN_NAME, POSTCODE]);
            if (!addressEditable) {
                updateGazetteerExtraFields(easting, northing, UPRN);
            }
            setFieldValue("applicantAddressLine1", line1);
            setFieldValue("applicantAddressLine2", line2);
            setFieldValue("applicantAddressLine3", line3);
            setFieldValue("applicantPostTown", TOWN_NAME);
            setFieldValue("applicantPostcode", POSTCODE);
            setFieldValue("applicantAddress", location);
        },
        [addressEditable, setFieldValue, updateGazetteerExtraFields]
    );
    return (
        <>
            <Fieldset>
                <Fieldset.Legend size="LARGE">Applicant details</Fieldset.Legend>
            </Fieldset>
            <GovField {...toFieldProps("applicantTitle", "TEXT")} />
            <GovField {...toFieldProps("applicantFirstName", "TEXT")} />
            <GovField {...toFieldProps("applicantSurname", "TEXT")} />
            <GovField {...toFieldProps("applicantPhoneNumber", "TEXT")} />
            <GovField {...toFieldProps("applicantEmail", "TEXT")} />
            <GovField {...toFieldProps("applicantCompany", "TEXT")} />
            <GovAddressBox onSelect={gazeteerOnSelect} />
            <GovField contentType="CHECKBOX" label="I want to enter the address by myself" value={addressEditable} onChange={toggleAddressEditable} />
            <FormGroup error={!!errorMsg} style={{ marginBottom: "0px" }}>
                <LabelText className="required">Address</LabelText>
                <ErrorText>{errorMsg}</ErrorText>
                <GovField {...toFieldProps("applicantAddressLine1", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
                <GovField {...toFieldProps("applicantAddressLine2", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
                <GovField {...toFieldProps("applicantAddressLine3", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
            </FormGroup>
            <GovField {...toFieldProps("applicantPostTown", "TEXT")} disabled={!addressEditable} />
            <GovField {...toFieldProps("applicantPostcode", "TEXT")} disabled={!addressEditable} />
            <Box display="flex" justifyContent="flex-end">
                <Button onClick={next}>Next</Button>
            </Box>
        </>
    );
});
